import { useState, useEffect, useCallback } from "react";

interface CachedReport {
  id: string;
  report_month: string;
  sentinela_index: number;
  monthly_revenue: number;
  food_beverage_cost: number;
  fixed_costs: number;
  other_costs: number | null;
  analysis_report: any;
  created_at: string;
  cached_at: number;
}

const CACHE_KEY = "sentinela_offline_reports";
const CACHE_EXPIRY = 7 * 24 * 60 * 60 * 1000; // 7 days

export function useOfflineCache() {
  const [isOnline, setIsOnline] = useState(navigator.onLine);
  const [cachedReports, setCachedReports] = useState<CachedReport[]>([]);

  useEffect(() => {
    const handleOnline = () => setIsOnline(true);
    const handleOffline = () => setIsOnline(false);

    window.addEventListener("online", handleOnline);
    window.addEventListener("offline", handleOffline);

    // Load cached reports on mount
    loadCachedReports();

    return () => {
      window.removeEventListener("online", handleOnline);
      window.removeEventListener("offline", handleOffline);
    };
  }, []);

  const loadCachedReports = useCallback(() => {
    try {
      const cached = localStorage.getItem(CACHE_KEY);
      if (cached) {
        const reports: CachedReport[] = JSON.parse(cached);
        // Filter out expired reports
        const validReports = reports.filter(
          (r) => Date.now() - r.cached_at < CACHE_EXPIRY
        );
        setCachedReports(validReports);
        // Update storage with valid reports only
        if (validReports.length !== reports.length) {
          localStorage.setItem(CACHE_KEY, JSON.stringify(validReports));
        }
      }
    } catch (error) {
      console.error("Error loading cached reports:", error);
    }
  }, []);

  const cacheReport = useCallback((report: Omit<CachedReport, "cached_at">) => {
    try {
      const cached = localStorage.getItem(CACHE_KEY);
      const reports: CachedReport[] = cached ? JSON.parse(cached) : [];
      
      // Remove old version if exists
      const filtered = reports.filter((r) => r.id !== report.id);
      
      // Add new report with timestamp
      const newReport: CachedReport = {
        ...report,
        cached_at: Date.now(),
      };
      
      filtered.push(newReport);
      
      // Keep only last 12 reports (1 year)
      const sorted = filtered
        .sort((a, b) => new Date(b.report_month).getTime() - new Date(a.report_month).getTime())
        .slice(0, 12);
      
      localStorage.setItem(CACHE_KEY, JSON.stringify(sorted));
      setCachedReports(sorted);
    } catch (error) {
      console.error("Error caching report:", error);
    }
  }, []);

  const cacheMultipleReports = useCallback((reports: Omit<CachedReport, "cached_at">[]) => {
    try {
      const cached = localStorage.getItem(CACHE_KEY);
      let existingReports: CachedReport[] = cached ? JSON.parse(cached) : [];
      
      reports.forEach((report) => {
        existingReports = existingReports.filter((r) => r.id !== report.id);
        existingReports.push({
          ...report,
          cached_at: Date.now(),
        });
      });
      
      // Keep only last 12 reports
      const sorted = existingReports
        .sort((a, b) => new Date(b.report_month).getTime() - new Date(a.report_month).getTime())
        .slice(0, 12);
      
      localStorage.setItem(CACHE_KEY, JSON.stringify(sorted));
      setCachedReports(sorted);
    } catch (error) {
      console.error("Error caching reports:", error);
    }
  }, []);

  const getCachedReport = useCallback((reportMonth: string): CachedReport | null => {
    const report = cachedReports.find((r) => r.report_month === reportMonth);
    return report || null;
  }, [cachedReports]);

  const clearCache = useCallback(() => {
    localStorage.removeItem(CACHE_KEY);
    setCachedReports([]);
  }, []);

  return {
    isOnline,
    cachedReports,
    cacheReport,
    cacheMultipleReports,
    getCachedReport,
    clearCache,
    loadCachedReports,
  };
}
